import { CheckCircleIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import { QuizAnswer, QuizResult } from "@/hooks/useQuizState";

interface QuizComponentProps {
  quiz: any;
  quizAnswers: QuizAnswer[];
  currentQuizQuestionIdx: number;
  isSubmittingQuiz: boolean;
  quizError: string | null;
  quizResult: QuizResult | null;
  onAnswerChange: (questionIdx: number, answer: string) => void;
  onPreviousQuestion: () => void;
  onNextQuestion: () => void;
  onQuizSubmit: () => void;
  tutorialTitle: string;
  language: string;
}

const QuizComponent = ({
  quiz,
  quizAnswers,
  currentQuizQuestionIdx,
  isSubmittingQuiz,
  quizError,
  quizResult,
  onAnswerChange,
  onPreviousQuestion,
  onNextQuestion,
  onQuizSubmit,
  tutorialTitle,
  language,
}: QuizComponentProps) => {
  const { t } = useTranslation();

  // Custom CSS for selected answer styling with maximum specificity
  const selectedAnswerStyle = `
    .quiz-answer-option.selected-answer-green {
      background-color: #16a34a !important;
      border-color: #16a34a !important;
      color: #ffffff !important;
      background: #16a34a !important;
      border: 2px solid #16a34a !important;
    }
    .quiz-answer-option.selected-answer-green * {
      color: #ffffff !important;
    }
    .quiz-answer-option.selected-answer-green span,
    .quiz-answer-option.selected-answer-green div {
      color: #ffffff !important;
    }
    .quiz-answer-option.selected-answer-green .CheckCircleIcon {
      color: #ffffff !important;
    }
    /* Override dark mode styles */
    .dark .quiz-answer-option.selected-answer-green {
      background-color: #16a34a !important;
      border-color: #16a34a !important;
      color: #ffffff !important;
      background: #16a34a !important;
      border: 2px solid #16a34a !important;
    }
    .dark .quiz-answer-option.selected-answer-green * {
      color: #ffffff !important;
    }
    /* Force override any conflicting styles */
    .quiz-answer-option.selected-answer-green,
    .dark .quiz-answer-option.selected-answer-green {
      background-color: #16a34a !important;
      border-color: #16a34a !important;
      color: #ffffff !important;
      background: #16a34a !important;
      border: 2px solid #16a34a !important;
      box-shadow: 0 4px 6px -1px rgba(22, 163, 74, 0.3) !important;
    }
  `;

  if (quizResult) {
    return (
      <div className="mt-6 sm:mt-8 p-4 sm:p-6 bg-gradient-to-r from-primary-50 to-primary-100 dark:from-primary-900/20 dark:to-primary-800/20 rounded-xl sm:rounded-2xl border border-primary-200/50 dark:border-primary-700/50">
        <div className="text-center mb-4 sm:mb-6">
          <div className="inline-flex items-center px-3 sm:px-4 py-1.5 sm:py-2 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-full text-xs sm:text-sm font-medium mb-2 sm:mb-3">
            <svg
              className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            {t("courses.quizResult")}
          </div>
          <h3 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-primary-600 to-primary-800 dark:from-primary-400 dark:to-primary-600 bg-clip-text text-transparent">
            Quiz Results
          </h3>
        </div>

        {quizResult.success && !quizResult.error ? (
          <div className="bg-primary-100 dark:bg-primary-900/30 border border-primary-200 dark:border-primary-700 rounded-lg sm:rounded-xl p-3 sm:p-4 mb-3 sm:mb-4">
            <div className="flex items-center text-primary-700 dark:text-primary-300">
              <svg
                className="w-4 h-4 sm:w-5 sm:h-5 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
              <span className="text-sm sm:text-base font-semibold">
                {t("courses.quizSubmitted")}
              </span>
            </div>
          </div>
        ) : null}

        {quizResult.error ? (
          <div className="bg-red-100 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg sm:rounded-xl p-3 sm:p-4">
            <div className="flex items-center text-red-700 dark:text-red-300">
              <svg
                className="w-4 h-4 sm:w-5 sm:h-5 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span className="text-sm sm:text-base font-semibold">
                {quizResult.error}
              </span>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3 lg:gap-4">
            <div className="bg-white dark:bg-gray-800 p-3 sm:p-4 rounded-lg sm:rounded-xl border border-primary-200/50 dark:border-primary-700/50 text-center">
              <div className="text-lg sm:text-xl lg:text-2xl font-bold text-primary-600 dark:text-primary-400 mb-1">
                {quizResult.score}
              </div>
              <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                Score
              </div>
              <div className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-500 mt-1">
                out of{" "}
                {quizResult.totalQuestions ||
                  quiz?.questions?.length ||
                  0}
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 p-3 sm:p-4 rounded-lg sm:rounded-xl border border-primary-200/50 dark:border-primary-700/50 text-center">
              <div className="text-lg sm:text-xl lg:text-2xl font-bold text-primary-600 dark:text-primary-400 mb-1">
                {quizResult.percentage}%
              </div>
              <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                Percentage
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 p-3 sm:p-4 rounded-lg sm:rounded-xl border border-primary-200/50 dark:border-primary-700/50 text-center">
              <div
                className={`text-sm sm:text-base lg:text-lg font-bold mb-1 ${
                  (quizResult.percentage ?? 0) >= 70
                    ? "text-primary-600 dark:text-primary-400"
                    : "text-red-600 dark:text-red-400"
                }`}
              >
                {(quizResult.percentage ?? 0) >= 70
                  ? "PASSED"
                  : "FAILED"}
              </div>
              <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                Status
              </div>
            </div>
          </div>
        )}

        {quizResult.feedback && (
          <div className="mt-4 sm:mt-6 bg-white dark:bg-gray-800 p-3 sm:p-4 rounded-lg sm:rounded-xl border border-gray-200/50 dark:border-gray-700/50">
            <h4 className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white mb-2">
              Feedback
            </h4>
            <p className="text-gray-600 dark:text-gray-300 text-xs sm:text-sm leading-relaxed">
              {quizResult.feedback}
            </p>
          </div>
        )}
      </div>
    );
  }

  if (!quiz || !quiz.questions) {
    return null;
  }

  const currentQuestion = quiz.questions[currentQuizQuestionIdx];
  const questionText = currentQuestion.question?.[language] || currentQuestion.question?.en || "";
  const options = currentQuestion.options?.[language] || currentQuestion.options?.en || [];

  return (
    <>
      <style>{selectedAnswerStyle}</style>
      <div className="flex flex-col items-center justify-center min-h-[50vh] sm:min-h-[60vh] px-2 sm:px-4">
        <div className="w-full max-w-lg bg-white dark:bg-gray-900 rounded-xl sm:rounded-2xl shadow-xl p-3 sm:p-6 lg:p-8">
          <div className="bg-gradient-to-r from-primary-600 to-primary-700 text-white p-3 sm:p-4 lg:p-6 rounded-t-lg -mt-3 sm:-mt-4 lg:-mt-6 -mx-3 sm:-mx-4 lg:-mx-6 mb-3 sm:mb-4 lg:mb-6">
            <h2 className="text-base sm:text-lg lg:text-2xl font-bold text-center mb-2 break-words">
              {t("courses.quizOn")} {tutorialTitle}
            </h2>
            <div className="text-center text-xs sm:text-sm text-primary-100">
              Question {currentQuizQuestionIdx + 1} of {quiz.questions?.length || 0}
            </div>
          </div>

          <div className="text-sm sm:text-base lg:text-xl font-bold mb-2 sm:mb-3 text-gray-900 dark:text-white break-words">
            {questionText || `Question ${currentQuizQuestionIdx + 1}`}
          </div>
          <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-3 sm:mb-4 lg:mb-6 font-medium">
            {t("courses.chooseOnlyOneAnswer")}
          </div>

          <div className="flex flex-col gap-2 sm:gap-3 lg:gap-4 mb-4 sm:mb-6 lg:mb-8">
            {options.map((opt: string, oidx: number) => {
              const selectedAnswer = quizAnswers[currentQuizQuestionIdx]?.selectedAnswer;
              // Handle both string and object formats for selectedAnswer
              const isSelected = typeof selectedAnswer === 'string' 
                ? selectedAnswer === opt 
                : selectedAnswer?.en === opt || selectedAnswer?.am === opt;
              return (
                <label
                  key={oidx}
                  className={`quiz-answer-option flex items-start sm:items-center px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg border-2 cursor-pointer transition-all duration-200 font-medium text-xs sm:text-sm lg:text-base relative
                    ${
                      isSelected
                        ? "selected-answer-green"
                        : "border-gray-300 bg-white dark:bg-gray-800 text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-gray-400"
                    }
                  `}
                  style={
                    isSelected
                      ? {
                          backgroundColor: "#16a34a !important",
                          borderColor: "#16a34a !important",
                          color: "white !important",
                          background: "#16a34a !important",
                          border: "2px solid #16a34a !important",
                        }
                      : {}
                  }
                >
                  <input
                    type="radio"
                    name={`quiz-question-${currentQuizQuestionIdx}`}
                    value={opt}
                    checked={isSelected}
                    onChange={() => onAnswerChange(currentQuizQuestionIdx, opt)}
                    className="mr-2 sm:mr-3 h-4 w-4 sm:h-5 sm:w-5 text-primary-600 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 border-gray-300 rounded-full flex-shrink-0 mt-0.5 sm:mt-0"
                  />
                  <span className="flex-1 break-words leading-relaxed">{opt}</span>
                  {isSelected && (
                    <div className="flex items-center ml-2 flex-shrink-0">
                      <CheckCircleIcon className="h-4 w-4 sm:h-5 sm:w-5 lg:h-6 lg:w-6 text-white" />
                      <span className="ml-1 text-xs sm:text-sm font-semibold text-white hidden sm:inline">
                        Selected
                      </span>
                    </div>
                  )}
                </label>
              );
            })}
          </div>

          <div className="flex flex-col sm:flex-row justify-between mt-4 sm:mt-6 lg:mt-8 gap-2 sm:gap-4">
            <button
              className={`flex-1 sm:flex-none px-3 sm:px-4 lg:px-6 py-2 sm:py-2.5 lg:py-3 rounded-lg text-xs sm:text-sm font-semibold transition-all duration-200 ${
                currentQuizQuestionIdx === 0
                  ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                  : "bg-gray-100 text-gray-700 border-2 border-gray-300 hover:bg-gray-200 hover:border-gray-400 hover:shadow-md"
              }`}
              onClick={onPreviousQuestion}
              disabled={currentQuizQuestionIdx === 0}
            >
              {t("common.previous")}
            </button>

            {currentQuizQuestionIdx < quiz.questions.length - 1 ? (
              <button
                className={`flex-1 sm:flex-none px-3 sm:px-4 lg:px-6 py-2 sm:py-2.5 lg:py-3 rounded-lg text-xs sm:text-sm font-semibold transition-all duration-200 ${
                  (() => {
                    const selectedAnswer = quizAnswers[currentQuizQuestionIdx]?.selectedAnswer;
                    return typeof selectedAnswer === 'string' 
                      ? !!selectedAnswer 
                      : !!(selectedAnswer?.en || selectedAnswer?.am);
                  })()
                    ? "bg-primary-600 text-white hover:bg-primary-700 hover:shadow-lg transform hover:scale-105 border-2 border-primary-600"
                    : "bg-gray-300 text-gray-500 cursor-not-allowed border-2 border-gray-300"
                }`}
                onClick={onNextQuestion}
                disabled={(() => {
                  const selectedAnswer = quizAnswers[currentQuizQuestionIdx]?.selectedAnswer;
                  return typeof selectedAnswer === 'string' 
                    ? !selectedAnswer 
                    : !(selectedAnswer?.en || selectedAnswer?.am);
                })()}
              >
                {t("common.next")}
              </button>
            ) : (
              <button
                className="flex-1 sm:flex-none px-4 sm:px-6 lg:px-8 py-2 sm:py-2.5 lg:py-3 rounded-lg text-xs sm:text-sm font-semibold bg-primary-600 text-white hover:bg-primary-700 hover:shadow-lg transform hover:scale-105 border-2 border-primary-600 transition-all duration-200"
                onClick={onQuizSubmit}
                disabled={isSubmittingQuiz}
              >
                {isSubmittingQuiz ? "Submitting..." : t("common.submit")}
              </button>
            )}
          </div>

          {quizError && (
            <div className="mb-3 sm:mb-4 text-red-500 text-center text-sm sm:text-base font-semibold">
              {quizError}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default QuizComponent;
